// ---------------------------------------------------------------------------
// LES TOUCHES DU LECTEUR, DÉCLARÉES UNE FOIS
//
// Elles vivaient dans un objet anonyme au milieu d'un écouteur `keydown` : neuf
// touches, aucun moyen de les éprouver, et rien qui puisse les AFFICHER. Or un
// lecteur dont les raccourcis ne se voient nulle part n'a, en pratique, pas de
// raccourcis : personne ne devine que « L » avance de trente secondes.
//
// La table est donc sortie ici, sous une forme qui sert aux deux usages :
//   · le lecteur y lit l'action à exécuter pour une frappe ;
//   · le panneau d'aide y lit la liste à montrer, groupée, dans l'ordre.
//
// ⚠️ ON LIT `event.key`, JAMAIS `code` NI `keyCode`.
//
// `key` porte le caractère RÉELLEMENT produit par la frappe, disposition du
// clavier comprise. C'est ce qui fait que « < » et « > » marchent sur un clavier
// français (touche dédiée à gauche du W) comme sur un QWERTY (Shift + virgule) :
// dans les deux cas la frappe produit le même caractère, et c'est lui qu'on
// regarde. Passer par `code` reviendrait à coder en dur une disposition — le
// piège classique de « ZQSD qui devient WASD ».
//
// Ce module ne touche à rien : il ne connaît ni le DOM, ni la balise vidéo, ni
// la séance. Il dit QUOI faire ; le lecteur décide s'il le peut.
// ---------------------------------------------------------------------------

/**
 * Ce que le lecteur sait faire au clavier.
 *
 * `touches` liste les caractères qui déclenchent l'action. `valeur` est passée
 * telle quelle à l'action (secondes, fraction de volume, pas de vitesse).
 * `groupe` et `libelle` n'existent que pour le panneau d'aide.
 */
export const RACCOURCIS = [
  // --- lecture -------------------------------------------------------------
  { action: 'lecture', touches: [' ', 'k'], groupe: 'Lecture', libelle: 'Lecture / pause', montre: 'Espace' },
  { action: 'avancer', touches: ['ArrowRight'], valeur: 10, groupe: 'Lecture', libelle: 'Avancer de 10 s', montre: '→' },
  { action: 'reculer', touches: ['ArrowLeft'], valeur: 10, groupe: 'Lecture', libelle: 'Reculer de 10 s', montre: '←' },
  { action: 'avancer', touches: ['l'], valeur: 30, groupe: 'Lecture', libelle: 'Avancer de 30 s', montre: 'L' },
  { action: 'reculer', touches: ['j'], valeur: 30, groupe: 'Lecture', libelle: 'Reculer de 30 s', montre: 'J' },
  { action: 'debut', touches: ['Home'], groupe: 'Lecture', libelle: 'Revenir au début', montre: 'Début' },
  { action: 'fin', touches: ['End'], groupe: 'Lecture', libelle: 'Aller à la fin', montre: 'Fin' },

  // --- vitesse -------------------------------------------------------------
  // ⚠️ VLC et YouTube ne s'accordent pas : le premier prend « + » et « - », le
  // second « > » et « < ». Les deux marchent ici — se tromper de réflexe ne
  // doit rien coûter, et aucune des quatre touches n'est prise ailleurs.
  { action: 'plusVite', touches: ['>', '+'], groupe: 'Vitesse', libelle: 'Accélérer', montre: '>' },
  { action: 'moinsVite', touches: ['<', '-'], groupe: 'Vitesse', libelle: 'Ralentir', montre: '<' },
  { action: 'vitesseNormale', touches: ['='], groupe: 'Vitesse', libelle: 'Revenir à 1×', montre: '=' },

  // --- son -----------------------------------------------------------------
  { action: 'muet', touches: ['m'], groupe: 'Son', libelle: 'Couper le son', montre: 'M' },
  { action: 'plusFort', touches: ['ArrowUp', 'AudioVolumeUp'], valeur: 0.1, groupe: 'Son', libelle: 'Monter le son', montre: '↑' },
  { action: 'moinsFort', touches: ['ArrowDown', 'AudioVolumeDown'], valeur: 0.1, groupe: 'Son', libelle: 'Baisser le son', montre: '↓' },

  // --- sous-titres ---------------------------------------------------------
  // `g`/`h` sont les touches de VLC pour le décalage. `c` est celle de YouTube
  // pour l'affichage.
  { action: 'basculerST', touches: ['c'], groupe: 'Sous-titres', libelle: 'Afficher / masquer', montre: 'C' },
  { action: 'stEnRetard', touches: ['h'], valeur: 0.1, groupe: 'Sous-titres', libelle: 'Retarder de 100 ms', montre: 'H' },
  { action: 'stEnAvance', touches: ['g'], valeur: 0.1, groupe: 'Sous-titres', libelle: 'Avancer de 100 ms', montre: 'G' },

  // --- écran ---------------------------------------------------------------
  { action: 'plein', touches: ['f'], groupe: 'Écran', libelle: 'Plein écran', montre: 'F' },
  { action: 'vignette', touches: ['i'], groupe: 'Écran', libelle: 'Image dans l’image', montre: 'I' },
  { action: 'aide', touches: ['?'], groupe: 'Écran', libelle: 'Cette liste', montre: '?' },
];

/** Les chiffres se placent en proportion : 3 → 30 % du film. Comme partout. */
const CHIFFRES = /^[0-9]$/;

/**
 * L'action à exécuter pour une frappe, ou `null` si la touche ne nous regarde
 * pas — auquel cas l'appelant NE DOIT PAS appeler `preventDefault`, sous peine
 * de confisquer des touches au reste de la page.
 *
 * @param {{key: string, ctrlKey?: boolean, metaKey?: boolean, altKey?: boolean}} e
 * @returns {{action: string, valeur?: number}|null}
 */
export function actionPour(e) {
  if (!e || typeof e.key !== 'string') return null;

  // ⚠️ UNE TOUCHE AVEC CTRL, META OU ALT APPARTIENT AU NAVIGATEUR.
  //
  // Ctrl+F cherche dans la page, Ctrl+L va à la barre d'adresse, Alt+← revient
  // en arrière. Les intercepter parce que « f » et « l » sont dans notre table
  // volerait des gestes que personne ne nous a confiés — et `preventDefault`
  // les rendrait silencieusement inopérants.
  if (e.ctrlKey || e.metaKey || e.altKey) return null;

  if (CHIFFRES.test(e.key)) {
    return { action: 'proportion', valeur: Number(e.key) / 10 };
  }

  // La casse ne compte pas : « L » majuscule est la même intention que « l ».
  // Les noms de touches (`ArrowRight`, `Home`) se comparent tels quels — les
  // mettre en minuscules ne les rendrait comparables à rien.
  for (const r of RACCOURCIS) {
    for (const t of r.touches) {
      const pareil = t.length === 1
        ? t.toLowerCase() === e.key.toLowerCase()
        : t === e.key;
      if (pareil) return r.valeur === undefined ? { action: r.action } : { action: r.action, valeur: r.valeur };
    }
  }
  return null;
}

/**
 * Les raccourcis groupés pour l'affichage, dans l'ordre de la table.
 *
 * Deux entrées de même action mais de valeurs différentes (avancer de 10 s et
 * de 30 s) restent deux lignes : c'est bien deux gestes distincts pour qui lit.
 */
export function raccourcisAffichables() {
  const groupes = [];
  for (const r of RACCOURCIS) {
    let g = groupes.find((x) => x.nom === r.groupe);
    if (!g) { g = { nom: r.groupe, lignes: [] }; groupes.push(g); }
    g.lignes.push({ touche: r.montre, libelle: r.libelle });
  }
  // Les chiffres ne sont pas dans la table — ils se calculent — mais ils
  // doivent se lire comme les autres.
  groupes.find((g) => g.nom === 'Lecture')?.lignes.push({
    touche: '0 – 9', libelle: 'Aller à 0 %, 10 %… du film',
  });
  return groupes;
}

// ---------------------------------------------------------------------------
// LA VITESSE
//
// ⚠️ ELLE NE PEUT PAS ÊTRE LIBRE PENDANT UNE SÉANCE.
//
// La séance garde toute une salle sur la même image : quand quelqu'un dérive,
// elle corrige en jouant 7 % plus vite ou plus lentement jusqu'à ce que l'écart
// se résorbe. Lire à 1,5× pendant ce temps, c'est dériver à chaque seconde plus
// vite que la correction ne rattrape : on se ferait ramener en arrière sans
// fin, et le film deviendrait injouable pour soi comme pour la salle.
//
// Le lecteur refuse donc le changement pendant une séance et le dit. Seul,
// c'est libre. Ce n'est pas une limite technique qu'on contourne un jour : deux
// personnes qui ne regardent pas à la même vitesse ne regardent pas ensemble.
// ---------------------------------------------------------------------------

/** Les crans, comme VLC : on ne tape pas 1,37×. */
export const VITESSES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

/**
 * La vitesse voisine, dans la direction demandée. Aux extrémités, on reste :
 * une touche qui ne fait rien vaut mieux qu'une vitesse absurde.
 *
 * @param {number} actuelle
 * @param {number} sens `+1` pour accélérer, `-1` pour ralentir.
 */
export function vitesseSuivante(actuelle, sens) {
  const v = Number(actuelle);
  const base = Number.isFinite(v) && v > 0 ? v : 1;
  // La vitesse courante peut ne pas être un cran : la séance écrit 1,07 pour
  // rattraper. On part alors du cran le plus proche, sans quoi « accélérer »
  // depuis 1,07 renverrait 1,25 dans un cas et 1,5 dans l'autre selon la
  // dérive du moment.
  let i = 0;
  for (let k = 1; k < VITESSES.length; k += 1) {
    if (Math.abs(VITESSES[k] - base) < Math.abs(VITESSES[i] - base)) i = k;
  }
  const j = Math.min(VITESSES.length - 1, Math.max(0, i + (sens > 0 ? 1 : -1)));
  return VITESSES[j];
}
